import { ElementWithUserNames, ElementInfo } from '../types/element.types';
import { ElementRelationDataCache, ElementDataCache } from '../types/cache.types';

/**
 * 根据 sceneId 从缓存中获取该场次的场次元素列表
 * 
 * 流程：
 * 1. 从 ElementRelationCache 过滤出 sceneID === sceneId 的关系
 * 2. 从 ElementCache 根据 elementID 获取完整的元素信息
 * 3. 合并关系字段和元素字段，组装成 ElementWithUserNames 格式
 * 
 * @param sceneId 场次ID
 * @param breakdownId 分场ID
 * @param relationCache ElementRelationCache（整个分场的关系缓存）
 * @param itemCache ElementCache（整个分场的元素缓存）
 * @returns 该场次的场次元素列表
 */
export const getElementsFromCache = (
  sceneId: string,
  breakdownId: string,
  relationCache: ElementRelationDataCache | null,
  itemCache: ElementDataCache | null
): ElementWithUserNames[] => {
  if (!relationCache?.elementRelations || !itemCache?.elements) {
    return [];
  }

  // 1. 从关系缓存中过滤出属于该场次的关系
  const sceneRelations = relationCache.elementRelations.filter(
    rel => rel.sceneID === sceneId
  );

  if (sceneRelations.length === 0) {
    return [];
  }

  // 2. 创建 elementID -> ElementInfo 的映射
  const elementMap = new Map<string, ElementInfo>();
  itemCache.elements.forEach(item => {
    if (item.elementID) {
      elementMap.set(item.elementID, item);
    }
  });

  // 3. 组装数据：合并 ElementInfo 和关系字段
  const elements: ElementWithUserNames[] = sceneRelations
    .map(rel => {
      const element = elementMap.get(rel.elementID);
      if (!element) {
        // 如果找不到对应的 element，跳过
        return null;
      }

      // 合并 ElementInfo 和关系字段
      return {
        ...element, // 包含所有 ElementInfo 字段（包括 elementName, elementAbbr, elementType, elementNO 等）
        // 关系字段（只使用关系表中的值）
        elementQtyInScene: rel.elementQtyInScene,
        elementBelongBreakdownID: rel.elementBelongBreakdownID || element.elementBelongBreakdownID,
        // 注意：elementType、elementName、elementAbbr、elementNO 这些字段已废弃，不再同步
        // 应该始终使用 element 表的值（通过 ...element 展开已包含）
        // 关系表中的这些字段可能包含过时的数据，不应该使用
      } as ElementWithUserNames;
    })
    .filter((item): item is ElementWithUserNames => item !== null);

  return elements;
};

